// Squirrel rig — bone topology and rest pose. Built procedurally from
// parameters; no GLB asset. The rig builder *is* the per-squirrel
// generator the seed-driven future needs (per
// DOCS/UNDER_REVIEW/aesthetic_pillars.md).
//
// Rig contract:
//   Hip → Spine → Chest → Neck → Head           (5-bone back chain)
//   Chest →  LegFL/LegFR (Hip → Knee → Foot)    (front legs branch off chest)
//   Hip   →  LegBL/LegBR (Hip → Knee → Foot)    (back legs branch off hip)
//   Hip   →  Tail_01 → … → Tail_05              (5-segment tail)
//   Head  →  EarL/EarR (Root → Tip)             (two-bone ears)
//
// Convention: +Y is up, +Z is the squirrel's forward, +X is to its right.
// Rest pose has the squirrel in a low crouch: hip held high by long
// back legs, shoulders/chest dropped by shorter front legs, back chain
// pitched so the chest hangs lower than the hip (the iconic high-rump
// silhouette), neck cocked up, head looking forward.
//
// The `Rig` returned object holds the root Group and named refs to every
// bone. Visuals are NOT built here — the tool component renders the rig
// however it likes (debug spheres, capsule meshes, eventual SkinnedMesh).

import * as THREE from 'three';

export interface RigParams {
  /** Hip → Spine, the lower-back segment. */
  spineLength: number;
  /** Spine → Chest, the upper-back segment. */
  chestLength: number;
  /** Chest → Neck. */
  neckLength: number;
  /** Neck → Head. */
  headLength: number;
  /** Front leg upper segment (Hip → Knee). Squirrels have short front legs. */
  frontLegUpperLength: number;
  /** Front leg lower segment (Knee → Foot). */
  frontLegLowerLength: number;
  /** Back leg upper segment (Hip → Knee). Long, powerful — drives hops. */
  backLegUpperLength: number;
  /** Back leg lower segment (Knee → Foot). */
  backLegLowerLength: number;
  /** Half-distance between the two front legs (X offset from chest). */
  frontLegHalfWidth: number;
  /** Half-distance between the two back legs (X offset from hip). */
  backLegHalfWidth: number;
  /** Number of tail segments. Spec calls for 5; tunable for variation. */
  tailSegments: number;
  /** Length of each tail segment. */
  tailSegmentLength: number;
  /** Half-distance between the two ears (X offset from head). */
  earHalfWidth: number;
  /** Length of each ear segment (root and tip). */
  earSegmentLength: number;
  /**
   * Vertical lift of neck above chest (the head sits up, not just forward).
   * Small fraction of neckLength.
   */
  neckLift: number;
  /** Vertical lift of head above neck. */
  headLift: number;
  /**
   * Standing-height factor in [0, 1]. 1 = back legs straight; lower =
   * crouched. Squirrels at rest sit deep in their hips; ~0.7 reads as
   * "alert hunch," ~0.55 reads as "settled."
   */
  crouchFactor: number;
  /**
   * Rest-pose pitch of the back chain leaving the hip (rad). Negative
   * lifts the spine above the hip — the squirrel rump-arch. Affects only
   * the back chain; back legs and tail parent to hip directly and are
   * unaffected.
   */
  hipPitch: number;
  /** Bend at the spine joint (rad). Positive drops chest below spine. */
  spineRestPitch: number;
  /** Rest-pose pitch of the chest (rad). Composes onto spine. */
  chestRestPitch: number;
  /** Rest-pose pitch of the neck (rad). Positive = head cocked up. */
  neckRestPitch: number;
  /** Rest-pose pitch of the head (rad). Composes with idle micro-noise. */
  headRestPitch: number;
  /**
   * Forward offset (body-local +Z) for the front-foot rest target relative
   * to the front-hip ground-projection. Positive = paws plant ahead.
   */
  frontFootForward: number;
  /**
   * Forward offset (body-local +Z) for the back-foot rest target relative
   * to the back-hip ground-projection. Squirrels plant their hind paws
   * ahead of the hip joint; positive values produce that pose.
   */
  backFootForward: number;
}

// Rest-pitch sign convention: each back-chain bone has its local +Z axis
// pointing forward toward its child. A positive rotation.x rotates that
// +Z toward -Y (XYZ Euler), so positive pitch on spine/chest *drops* the
// chain forward and down. Neck and head want to lift up, so they take
// negative pitch.
export const defaultRigParams: RigParams = {
  spineLength: 0.10,
  chestLength: 0.08,
  neckLength: 0.05,
  headLength: 0.05,
  frontLegUpperLength: 0.07,
  frontLegLowerLength: 0.07,
  backLegUpperLength: 0.10,
  backLegLowerLength: 0.10,
  frontLegHalfWidth: 0.04,
  backLegHalfWidth: 0.05,
  tailSegments: 9,
  tailSegmentLength: 0.035,
  earHalfWidth: 0.022,
  earSegmentLength: 0.03,
  neckLift: 0.04,
  headLift: 0.02,
  crouchFactor: 0.7,
  hipPitch: -0.20,
  spineRestPitch: 0.55,
  chestRestPitch: 0.10,
  neckRestPitch: -0.60,
  headRestPitch: 0.15,
  frontFootForward: 0.015,
  backFootForward: 0.04,
};

export interface LegBones {
  hip: THREE.Bone;
  knee: THREE.Bone;
  foot: THREE.Bone;
  /** Hip → Knee length, used by the IK solver. */
  upperLength: number;
  /** Knee → Foot length, used by the IK solver. */
  lowerLength: number;
}

export interface EarBones {
  root: THREE.Bone;
  tip: THREE.Bone;
}

export interface Rig {
  /** Root Group — add this to your scene; position it to place the rig. */
  root: THREE.Group;
  hip: THREE.Bone;
  spine: THREE.Bone;
  chest: THREE.Bone;
  neck: THREE.Bone;
  head: THREE.Bone;
  legFL: LegBones;
  legFR: LegBones;
  legBL: LegBones;
  legBR: LegBones;
  /** Tail bones, root → tip (length = params.tailSegments). */
  tail: THREE.Bone[];
  earL: EarBones;
  earR: EarBones;
  /** The params used to build this rig. Useful for IK chain lengths. */
  params: RigParams;
  /**
   * Hip's vertical position above the feet at rest. Equals
   * (backLegUpperLength + backLegLowerLength) * crouchFactor — the back
   * legs hold the rump up; the front legs reach down to whatever height
   * the rest-pose pitches drop the chest to.
   */
  standingHeight: number;
  /** Flat list of every bone, parent-before-child order. */
  allBones: THREE.Bone[];
}

export function buildRig(params: RigParams = defaultRigParams): Rig {
  const root = new THREE.Group();
  root.name = 'SquirrelRig';

  const standingHeight =
    (params.backLegUpperLength + params.backLegLowerLength) * params.crouchFactor;

  // Back chain: Hip → Spine → Chest → Neck → Head, running along +Z.
  // Rest-pose pitches are baked onto each bone's local rotation.x so they
  // compose under runtime overrides (yaw on turn, head idle pitch). Y/Z
  // axes stay free for runtime motion to write into.
  const hip = makeBone('Hip');
  hip.position.set(0, standingHeight, 0);
  root.add(hip);

  // Spine: positioned at the end of the hip→spine segment (pitched by
  // hipPitch), with its local frame aligned with that direction so the
  // spine joint's bend (spineRestPitch) is a relative flex from there.
  // Net world rotation around X = hipPitch + spineRestPitch.
  const spine = makeBone('Spine');
  const sH = Math.sin(params.hipPitch);
  const cH = Math.cos(params.hipPitch);
  spine.position.set(0, -sH * params.spineLength, cH * params.spineLength);
  spine.rotation.x = params.hipPitch + params.spineRestPitch;
  hip.add(spine);

  const chest = makeBone('Chest');
  chest.position.set(0, 0, params.chestLength);
  chest.rotation.x = params.chestRestPitch;
  spine.add(chest);

  const neck = makeBone('Neck');
  neck.position.set(0, params.neckLift, params.neckLength);
  neck.rotation.x = params.neckRestPitch;
  chest.add(neck);

  const head = makeBone('Head');
  head.position.set(0, params.headLift, params.headLength);
  head.rotation.x = params.headRestPitch;
  neck.add(head);

  // Front legs (children of Chest). Local-Y points down at rest, so the
  // knee sits below the hip and the foot below the knee.
  const legFL = makeLeg(
    chest, 'FL',
    -params.frontLegHalfWidth,
    params.frontLegUpperLength,
    params.frontLegLowerLength,
  );
  const legFR = makeLeg(
    chest, 'FR',
    +params.frontLegHalfWidth,
    params.frontLegUpperLength,
    params.frontLegLowerLength,
  );

  // Back legs (children of Hip). Same convention; offset out from spine.
  const legBL = makeLeg(
    hip, 'BL',
    -params.backLegHalfWidth,
    params.backLegUpperLength,
    params.backLegLowerLength,
  );
  const legBR = makeLeg(
    hip, 'BR',
    +params.backLegHalfWidth,
    params.backLegUpperLength,
    params.backLegLowerLength,
  );

  // Tail: chain off the back of Hip, extending in -Z.
  const tail: THREE.Bone[] = [];
  let tailParent: THREE.Bone = hip;
  for (let i = 0; i < params.tailSegments; i++) {
    const segment = makeBone(`Tail_${String(i + 1).padStart(2, '0')}`);
    // First segment offsets back from hip; subsequent ones offset back
    // from the previous segment.
    segment.position.set(0, 0, -params.tailSegmentLength);
    tailParent.add(segment);
    tail.push(segment);
    tailParent = segment;
  }

  // Ears: root attaches to the top of head, tip extends up.
  const earL = makeEar(head, 'L', -params.earHalfWidth, params);
  const earR = makeEar(head, 'R', +params.earHalfWidth, params);

  const allBones: THREE.Bone[] = [];
  collectBones(hip, allBones);

  return {
    root,
    hip,
    spine,
    chest,
    neck,
    head,
    legFL,
    legFR,
    legBL,
    legBR,
    tail,
    earL,
    earR,
    params,
    standingHeight,
    allBones,
  };
}

function makeBone(name: string): THREE.Bone {
  const bone = new THREE.Bone();
  bone.name = name;
  return bone;
}

function makeLeg(
  parent: THREE.Bone,
  side: 'FL' | 'FR' | 'BL' | 'BR',
  xOffset: number,
  upperLength: number,
  lowerLength: number,
): LegBones {
  const hip = makeBone(`Leg${side}_Hip`);
  hip.position.set(xOffset, 0, 0);
  parent.add(hip);

  const knee = makeBone(`Leg${side}_Knee`);
  knee.position.set(0, -upperLength, 0);
  hip.add(knee);

  const foot = makeBone(`Leg${side}_Foot`);
  foot.position.set(0, -lowerLength, 0);
  knee.add(foot);

  return { hip, knee, foot, upperLength, lowerLength };
}

function makeEar(
  parent: THREE.Bone,
  side: 'L' | 'R',
  xOffset: number,
  params: RigParams,
): EarBones {
  const root = makeBone(`Ear${side}_Root`);
  root.position.set(xOffset, params.earSegmentLength * 0.5, 0);
  parent.add(root);

  const tip = makeBone(`Ear${side}_Tip`);
  tip.position.set(0, params.earSegmentLength, 0);
  root.add(tip);

  return { root, tip };
}

function collectBones(node: THREE.Object3D, out: THREE.Bone[]): void {
  if ((node as THREE.Bone).isBone) out.push(node as THREE.Bone);
  for (const child of node.children) collectBones(child, out);
}
